import React from 'react';
import {HomeReel} from '../../../../services/socialFeedApi';
import {formatDuration, formatViewCount, getAssetUrl} from '../utils';
import HomeModalShell from './HomeModalShell';
import styles from '../Home.module.css';

const commentOutlineIconUrl = `${process.env.PUBLIC_URL}/icons/comment-outline.svg`;
const sendIconUrl = `${process.env.PUBLIC_URL}/icons/send.svg`;

interface ReelPlayerModalProps {
  reel: HomeReel | null;
  error: string | null;
  onClose: () => void;
  onPlay: () => void;
  onToggleLike: () => void;
  liking: boolean;
  onPlayerError: (message: string) => void;
  onOpenComments: (reel: HomeReel) => void;
  canShareToCoach?: boolean;
  onShareToCoach?: (reel: HomeReel) => void;
}

const HeartIcon: React.FC<{filled: boolean}> = ({filled}) => (
  <svg className={styles.playerActionIcon} viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M20.8 4.6c-2-2-5.2-2-7.2 0L12 6.2l-1.6-1.6c-2-2-5.2-2-7.2 0s-2 5.2 0 7.2L12 20.6l8.8-8.8c2-2 2-5.2 0-7.2z"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
  </svg>
);

const ReelPlayerModal: React.FC<ReelPlayerModalProps> = ({
  reel,
  error,
  onClose,
  onPlay,
  onToggleLike,
  liking,
  onPlayerError,
  onOpenComments,
  canShareToCoach = false,
  onShareToCoach,
}) => {
  if (!reel) {
    return null;
  }

  const reelVideoUrl = getAssetUrl(reel.videoUrl);
  const reelThumbnailUrl = getAssetUrl(reel.thumbnailUrl);
  const reelLikeCount = Math.max(0, reel.likeCount || 0);

  return (
    <HomeModalShell
      backdropClassName={styles.playerBackdrop}
      panelClassName={styles.playerCard}
      ariaLabelledBy="reel-player-modal-title"
      onClose={onClose}
    >
      <div className={styles.playerHeader}>
        <div>
          <h2 id="reel-player-modal-title" className={styles.playerTitle}>{reel.title}</h2>
          <p className={styles.playerMeta}>
            {reel.author.name} · {formatViewCount(reel.viewCount)} views · {formatDuration(reel.durationSeconds)}
          </p>
        </div>
        <button
          type="button"
          className={styles.playerClose}
          onClick={onClose}
          aria-label="Close reel player"
        >
          ×
        </button>
      </div>

      {reelVideoUrl ? (
        <div className={styles.playerVideoShell}>
          <video
            key={reel.reelId}
            className={styles.playerVideo}
            controls
            autoPlay
            playsInline
            poster={reelThumbnailUrl || undefined}
            onPlay={() => {
              void onPlay();
            }}
            onError={() => {
              onPlayerError('This reel could not be played right now.');
            }}
          >
            <source src={reelVideoUrl} />
            Your browser does not support the video player.
          </video>
        </div>
      ) : (
        <div className={styles.playerMessage}>This reel is missing its video source.</div>
      )}

      {error && <div className={styles.playerMessage}>{error}</div>}

      <div className={styles.playerActions}>
        <button
          type="button"
          className={`${styles.playerActionButton} ${styles.playerLikeButton} ${reel.hasLiked ? styles.playerLikeButtonActive : ''}`.trim()}
          aria-label={reel.hasLiked ? `Unlike ${reel.title}. ${reelLikeCount} likes.` : `Like ${reel.title}. ${reelLikeCount} likes.`}
          aria-pressed={reel.hasLiked}
          disabled={liking}
          onClick={onToggleLike}
        >
          <HeartIcon filled={reel.hasLiked} />
          {reel.hasLiked ? 'Liked' : 'Like'} {formatViewCount(reelLikeCount)}
        </button>
        <button
          type="button"
          className={styles.playerActionButton}
          onClick={() => onOpenComments(reel)}
        >
          <span
            className={`${styles.commentOutlineIcon} ${styles.playerActionIcon}`.trim()}
            style={{'--comment-outline-icon-url': `url("${commentOutlineIconUrl}")`} as React.CSSProperties}
            aria-hidden="true"
          />
          <span>Comments {reel.commentCount}</span>
        </button>
        {canShareToCoach && onShareToCoach ? (
          <button
            type="button"
            className={`${styles.playerActionButton} ${styles.playerShareButton}`.trim()}
            onClick={() => onShareToCoach(reel)}
          >
            <span
              className={`${styles.sendIcon} ${styles.playerActionIcon}`.trim()}
              style={{'--send-icon-url': `url("${sendIconUrl}")`} as React.CSSProperties}
              aria-hidden="true"
            />
            <span>Send to Coach</span>
          </button>
        ) : null}
      </div>
    </HomeModalShell>
  );
};

export default React.memo(ReelPlayerModal);
